import { api } from "../api/Middleware";
import { WABOTTY_URL } from "../api/Urls";

// ====================================================================
// 🔗 GESTÃO DE LINKS PÚBLICOS WHATSAPP - ADMIN
// ====================================================================
// 
// Este serviço gerencia a criação, listagem, atualização e exclusão
// de tokens/links públicos para conexão WhatsApp de agentes
//
// IMPORTANTE: Estes endpoints requerem AUTENTICAÇÃO de usuário admin
// ====================================================================

/**
 * 1. Listar todos os tokens públicos
 * 
 * @param {Object} params - Parâmetros de busca (page, page_size, search, status)
 * @returns {Object} Lista paginada de tokens
 */
export const getPublicLinks = async (params = {}) => {
  try {
    const searchParams = new URLSearchParams({
      page: params.page || 1,
      page_size: params.pageSize || 10,
      ...params
    });

    const response = await api.get(
      `${WABOTTY_URL}admin/public/?${searchParams}`
    );
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao buscar links públicos:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro ao buscar links",
        status: status
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500
    };
  }
};

/**
 * 2. Obter detalhes de um token público específico
 * 
 * @param {number} tokenId - ID do token
 * @returns {Object} Detalhes completos do token
 */
export const getPublicLinkDetails = async (tokenId) => {
  try {
    const response = await api.get(
      `${WABOTTY_URL}admin/public/${tokenId}/`
    );
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao buscar detalhes do link:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Link não encontrado",
        status: status
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500
    };
  }
};

/**
 * 3. Criar novo token público
 * 
 * @param {Object} linkData - Dados do token a ser criado (deve conter agent)
 * @returns {Object} Token criado com código de 6 dígitos gerado
 */
export const createPublicLink = async (linkData) => {
  try {
    const payload = {
      agent: linkData.agent
    };

    const response = await api.post(
      `${WABOTTY_URL}admin/public/`,
      payload
    );
    
    if (response.status === 201) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao criar link público:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro ao criar link",
        status: status,
        validationErrors: data.validation_errors || null
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500
    };
  }
};

/**
 * 4. Atualizar token público existente
 * 
 * @param {number} tokenId - ID do token
 * @param {Object} linkData - Dados atualizados
 * @returns {Object} Token atualizado
 */
export const updatePublicLink = async (tokenId, linkData) => {
  try {
    const payload = {
      is_active: linkData.isActive
    };

    const response = await api.put(
      `${WABOTTY_URL}admin/public/${tokenId}/`,
      payload
    );
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao atualizar link público:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro ao atualizar link",
        status: status,
        validationErrors: data.validation_errors || null
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500
    };
  }
};

/**
 * 5. Excluir token público
 * 
 * @param {number} tokenId - ID do token
 * @returns {Object} Confirmação da exclusão
 */
export const deletePublicLink = async (tokenId) => {
  try {
    const response = await api.delete(
      `${WABOTTY_URL}admin/public/${tokenId}/`
    );
    
    if (response.status === 204 || response.status === 200) {
      return {
        success: true,
        message: "Link excluído com sucesso"
      };
    }
  } catch (error) {
    console.error("Erro ao excluir link público:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro ao excluir link",
        status: status
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500
    };
  }
};

/**
 * 6. Ativar/Desativar token público
 * 
 * @param {number} tokenId - ID do token
 * @returns {Object} Token atualizado
 */
export const togglePublicLinkStatus = async (tokenId) => {
  try {
    const response = await api.post(
      `${WABOTTY_URL}admin/public/${tokenId}/toggle_active/`
    );
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao alterar status do link:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro ao alterar status",
        status: status
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500
    };
  }
};

/**
 * 7. Regenerar token público
 * 
 * @param {number} tokenId - ID do token
 * @returns {Object} Token regenerado
 */
export const regeneratePublicLinkToken = async (tokenId) => {
  try {
    const response = await api.post(
      `${WABOTTY_URL}admin/public/${tokenId}/regenerate/`
    );
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao regenerar token:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro ao regenerar token",
        status: status
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500
    };
  }
};

// Nota: Funções de estatísticas, logs e duplicação não estão disponíveis 
// na API administrativa conforme documentação fornecida

// ====================================================================
// FUNÇÕES HELPER E UTILITÁRIOS
// ====================================================================

/**
 * Formatar URL completa do link público
 * 
 * @param {string} token - Token de 6 dígitos
 * @param {string} baseUrl - URL base da aplicação
 * @returns {string} URL completa do link
 */
export const formatPublicLinkUrl = (token, baseUrl = window.location.origin) => {
  return `${baseUrl}/conectar/whatsapp/${token}`;
};

/**
 * Calcular tempo restante até expiração
 * 
 * @param {string} expiresAt - Data/hora de expiração (ISO string)
 * @returns {Object} Tempo restante formatado
 */
export const calculateTimeRemaining = (expiresAt) => {
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires.getTime() - now.getTime();

  if (diff <= 0) {
    return {
      expired: true,
      totalMinutes: 0,
      hours: 0,
      minutes: 0,
      formatted: "Expirado"
    };
  }

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let formatted;
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    formatted = `${days} dia${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    formatted = `${hours}h ${minutes}m`;
  } else {
    formatted = `${minutes}m`;
  }

  return {
    expired: false,
    totalMinutes,
    hours,
    minutes,
    formatted
  };
};

/**
 * Validar dados do formulário de token público
 * 
 * @param {Object} data - Dados do formulário
 * @returns {Object} Resultado da validação
 */
export const validatePublicLinkData = (data) => {
  const errors = {};

  // Agente obrigatório
  if (!data.agent) {
    errors.agent = "Agente é obrigatório";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Obter status legível do token
 * 
 * @param {Object} token - Objeto do token público
 * @returns {Object} Status formatado
 */
export const getLinkStatus = (token) => {
  const now = new Date();
  const expires = new Date(token.expires_at);
  const isExpired = now > expires;

  if (!token.is_active) {
    return {
      status: "inactive",
      label: "Inativo",
      color: "secondary",
      canUse: false
    };
  }

  if (isExpired) {
    return {
      status: "expired",
      label: "Expirado",
      color: "danger",
      canUse: false
    };
  }

  if (token.is_used) {
    return {
      status: "used",
      label: "Usado",
      color: "warning",
      canUse: false
    };
  }

  return {
    status: "active",
    label: "Ativo",
    color: "success",
    canUse: true
  };
};

// Constantes para uso nos componentes
export const LINK_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  EXPIRED: "expired",
  USED: "used"
};

export const DEFAULT_EXPIRY_HOURS = 0.5; // 30 minutos conforme documentação
export const TOKEN_LENGTH = 6;

const waPublicLinkServices = {
  getPublicLinks,
  getPublicLinkDetails,
  createPublicLink,
  updatePublicLink,
  deletePublicLink,
  togglePublicLinkStatus,
  regeneratePublicLinkToken,
  formatPublicLinkUrl,
  calculateTimeRemaining,
  validatePublicLinkData,
  getLinkStatus,
  LINK_STATUS,
  DEFAULT_EXPIRY_HOURS,
  TOKEN_LENGTH
};

export default waPublicLinkServices;